import { quantityError } from '@krp/sale-ui'
import { addProduct, isSameProduct, emptyCart, removeLine, setQuantity, type Cart, type SaleProduct } from './cart'
import { toMilli } from './money'
import type { SaleVerdict } from '@krp/client-runtime'

/**
 * Состояние продажи источника «Собственная»: корзину ведёт само окно (сервера корзины нет), оплату и чек пробивает кассовый агент.
 * Здесь только чистая часть — намерение кассира и этап продажи; данные агента (порог, смена, журнал) приходят из контроллера сессии,
 * товары — из каталога. Редьюсер ничего не отправляет: отправку делает хук, а итог возвращает действием `settled`.
 *
 * Этапы: `composing` — собираем корзину; `paying` — команда ушла агенту, ответа нет; `finished` — чек пробит; `failed` — не пробит
 * наверняка (можно исправить и повторить); `unknown` — исход неизвестен (чек не повторять, выяснить через агент).
 */

export type OwnPaymentKind = 'Cash' | 'Card'

export type OwnPhase = 'composing' | 'paying' | 'finished' | 'failed' | 'unknown'

export interface OwnSaleState {
  cart: Cart
  selectedLineId: number | null
  /** Набранное количество строки, пока оно не годится: поле показывает ввод и ошибку, корзина хранит последнее годное. */
  quantityDrafts: Readonly<Record<number, string>>
  paymentKind: OwnPaymentKind
  contact: string
  searchText: string
  /** Вид цены, выбранный кассиром; `null` — вид по умолчанию из настроек источника. Меняется только при пустой корзине. */
  priceTypeKey: string | null
  phase: OwnPhase
  /** Чем платили в команде, которая сейчас в пути или закончилась. */
  paidKind: OwnPaymentKind | null
  /** Итог чека в копейках на момент оплаты: корзина после неё не меняется, а итог на экране — тот, что ушёл агенту. */
  paidTotalKopecks: number | null
  verdict: SaleVerdict | null
}

export const initialOwnSale: OwnSaleState = {
  cart: emptyCart(),
  selectedLineId: null,
  quantityDrafts: {},
  paymentKind: 'Cash',
  contact: '',
  searchText: '',
  priceTypeKey: null,
  phase: 'composing',
  paidKind: null,
  paidTotalKopecks: null,
  verdict: null,
}

export type OwnSaleAction =
  | { type: 'product-added'; product: SaleProduct }
  | { type: 'quantity-typed'; lineId: number; text: string }
  | { type: 'quantity-stepped'; lineId: number; delta: 1 | -1 }
  | { type: 'line-removed'; lineId: number }
  | { type: 'line-selected'; lineId: number }
  | { type: 'payment-kind-chosen'; kind: OwnPaymentKind }
  | { type: 'contact-changed'; text: string }
  | { type: 'search-changed'; text: string }
  | { type: 'price-type-chosen'; key: string | null }
  | { type: 'cleared' }
  | { type: 'pay-started'; kind: OwnPaymentKind; totalKopecks: number }
  /** Команду не отправили (продажа оказалась закрыта): вернуться к корзине, ничего не потеряв. */
  | { type: 'pay-aborted' }
  | { type: 'settled'; verdict: SaleVerdict }
  | { type: 'new-sale' }
  /** Продажа с неизвестным исходом отложена агентом по слову кассира: касса свободна, корзина этой продажи закрыта. */
  | { type: 'parked' }
  /** Корзина возвращена из снимка начатой операции (после F5): цены, ставки и профили — из снимка, карточки не перечитываются. */
  | { type: 'restored'; cart: Cart; paymentKind: OwnPaymentKind }

/** Количество строкой для поля: «2», «2.5», «0.333». */
export function quantityText(milli: number): string {
  const whole = Math.floor(milli / 1000)
  const fraction = String(milli % 1000).padStart(3, '0').replace(/0+$/, '')
  return fraction === '' ? String(whole) : `${whole}.${fraction}`
}

/** Корзину можно менять только пока собираем. */
const editable = (state: OwnSaleState): boolean => state.phase === 'composing'

export function ownSaleReducer(state: OwnSaleState, action: OwnSaleAction): OwnSaleState {
  switch (action.type) {
    case 'product-added': {
      if (!editable(state)) return state
      const cart = addProduct(state.cart, action.product)
      // Выбрана строка, в которую только что попал товар: её же правят следом.
      const line = cart.lines.find((item) => isSameProduct(item, action.product))
      return { ...state, cart, selectedLineId: line?.id ?? state.selectedLineId, quantityDrafts: withoutDraft(state.quantityDrafts, line?.id) }
    }
    case 'quantity-typed': {
      if (!editable(state)) return state
      const line = state.cart.lines.find((item) => item.id === action.lineId)
      if (line === undefined) return state
      const drafts = { ...state.quantityDrafts, [action.lineId]: action.text }
      if (quantityError(action.text, line.isWeight) !== null) return { ...state, quantityDrafts: drafts }
      const milli = toMilli(action.text.trim().replace(',', '.'))
      if (milli === null) return { ...state, quantityDrafts: drafts }
      return { ...state, cart: setQuantity(state.cart, action.lineId, milli), quantityDrafts: withoutDraft(drafts, action.lineId) }
    }
    case 'quantity-stepped': {
      if (!editable(state)) return state
      const line = state.cart.lines.find((item) => item.id === action.lineId)
      if (line === undefined) return state
      // Шаг — одна целая единица; у дробного количества шаг тоже целый, но ниже единицы строка не уходит (убирают кнопкой).
      const next = line.quantityMilli + action.delta * 1000
      if (next < 1000) return state
      return { ...state, cart: setQuantity(state.cart, action.lineId, next), quantityDrafts: withoutDraft(state.quantityDrafts, action.lineId) }
    }
    case 'line-removed': {
      if (!editable(state)) return state
      const cart = removeLine(state.cart, action.lineId)
      return {
        ...state,
        cart,
        quantityDrafts: withoutDraft(state.quantityDrafts, action.lineId),
        selectedLineId: state.selectedLineId === action.lineId ? null : state.selectedLineId,
      }
    }
    case 'line-selected':
      return state.cart.lines.some((line) => line.id === action.lineId) ? { ...state, selectedLineId: action.lineId } : state
    case 'payment-kind-chosen':
      return editable(state) ? { ...state, paymentKind: action.kind } : state
    case 'contact-changed':
      return editable(state) ? { ...state, contact: action.text } : state
    case 'search-changed':
      return { ...state, searchText: action.text }
    // Вид цены меняется между чеками: цены строк уже в корзине, смешивать два вида цен в одном чеке нельзя.
    case 'price-type-chosen':
      return editable(state) && state.cart.lines.length === 0 ? { ...state, priceTypeKey: action.key } : state
    case 'cleared':
      return editable(state) ? { ...initialOwnSale, paymentKind: state.paymentKind, searchText: state.searchText, priceTypeKey: state.priceTypeKey } : state
    case 'pay-started':
      return editable(state) ? { ...state, phase: 'paying', paidKind: action.kind, paidTotalKopecks: action.totalKopecks, verdict: null } : state
    case 'pay-aborted':
      return state.phase === 'paying' ? { ...state, phase: 'composing', paidKind: null, paidTotalKopecks: null, verdict: null } : state
    case 'settled': {
      if (state.phase !== 'paying' && state.phase !== 'unknown') return state
      const phase: OwnPhase = action.verdict.kind === 'sold' ? 'finished' : action.verdict.kind === 'not-sold' ? 'failed' : 'unknown'
      return { ...state, phase, verdict: action.verdict }
    }
    case 'restored':
      // Только в пустую корзину на этапе сборки: уже набранное человеком снимок не затирает.
      return state.phase === 'composing' && state.cart.lines.length === 0 ? { ...state, cart: action.cart, paymentKind: action.paymentKind, selectedLineId: null, quantityDrafts: {} } : state
    case 'parked':
      return state.phase === 'unknown' ? { ...initialOwnSale, paymentKind: state.paymentKind, priceTypeKey: state.priceTypeKey } : state
    case 'new-sale':
      // Новая продажа начинается после итога; из «неизвестно» выходят словом человека: повторить проведение, закрыть или отложить (`parked`).
      return state.phase === 'finished' || state.phase === 'failed'
        ? state.phase === 'failed'
          ? { ...state, phase: 'composing', paidKind: null, paidTotalKopecks: null, verdict: null }
          : { ...initialOwnSale, paymentKind: state.paymentKind, priceTypeKey: state.priceTypeKey }
        : state
  }
}

function withoutDraft(drafts: Readonly<Record<number, string>>, lineId: number | undefined): Readonly<Record<number, string>> {
  if (lineId === undefined || !(lineId in drafts)) return drafts
  return Object.fromEntries(Object.entries(drafts).filter(([key]) => Number(key) !== lineId))
}
